import React, { useEffect, useState } from 'react';
import { Table, ColumnDefinition } from '@/UIlibrary/Table/Table';
import { BRAND_COLORS, GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { useTranslations } from 'use-intl';
import { Search } from 'react-feather';
import InputField from '@/UIlibrary/InputField/InputField';
import { Typography } from '@/stories/Library/Components/Typography/Typography';
import { gql, useQuery } from '@apollo/client';
import { RootQueryType, Registry, RegistryStatus } from '@/gql/graphql';
import { Link } from 'react-router-dom';
import { MexStates } from '@/utils/maps';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import { ClientStatus } from '@/types/Client/Client';

export const GET_CLIENTS_PROSPECTUS = gql`
	query GetClientsProspectus {
		listRegistries {
			results {
				id
				personalInformation {
					name
					firstLastName
				}
				birthInformation {
					stateOfBirth
				}
				status
			}
		}
	}
`;

interface ClientProspectusTableRow {
	clientId: string;
	name: string;
	stateOfBirth: string;
	status: RegistryStatus;
}

const ClientProspectusTable: React.FC = () => {
	const t = useTranslations('Clients');
	const { loading, data } = useQuery<RootQueryType>(GET_CLIENTS_PROSPECTUS);
	const [clients, setClients] = useState<ClientProspectusTableRow[]>([]);
	const [searchInput, setSearchInput] = useState<string>('');
	const [filteredClientsProspectus, setFilteredClientsProspectus] = useState<
		ClientProspectusTableRow[]
	>([]);

	useEffect(() => {
		if (data && data.listRegistries && data.listRegistries.results) {
			const clientProspectusList: ClientProspectusTableRow[] = [];
			const results: Registry[] = data.listRegistries.results;
			console.log(results);
			results.map((client) => {
				clientProspectusList.push({
					clientId: client.id,
					name: `${client.personalInformation ? client.personalInformation.name : ''} ${client.personalInformation ? client.personalInformation.firstLastName : ''}`,
					stateOfBirth: client.birthInformation?.stateOfBirth ?? '',
					status: client.status ? client.status : RegistryStatus.Created
				});
			});
			setClients(clientProspectusList);
		}
	}, [data]);

	const getSearchableFields = (client: any) => {
		return {
			id: client.clientId,
			name: client.name,
			state: MexStates[client.stateOfBirth as keyof typeof MexStates],
			status: client.status
		};
	};

	const searchInFields = (
		searchableFields: Record<string, string | undefined>,
		searchTerm: string
	) => {
		return Object.values(searchableFields)
			.filter(Boolean)
			.some((field) => field && field.toLowerCase().includes(searchTerm.toLowerCase()));
	};

	useEffect(() => {
		const filterClients = clients.filter((client) => {
			const fields = searchInFields(getSearchableFields(client), searchInput);
			return fields;
		});
		setFilteredClientsProspectus(filterClients);
	}, [clients, searchInput]);

	const ClientStatusDisplayName = (status: ClientStatus) => {
		const statusDisplayNames: Record<ClientStatus, string> = {
			BIRTH_INFORMATION_REGISTERED: t('BirthInformationStatus'),
			COMPLETED: t('CompletedStatus'),
			CREATED: t('CreatedStatus'),
			OFFICIAL_ADDRESS_REGISTERED: t('OfficialAddressStatus'),
			PERSONAL_INFORMATION_REGISTERED: t('PersonalInformationStatus'),
			TAX_INFORMATION_REGISTERED: t('TaxInformationStatus')
		};
		return statusDisplayNames[status] || '';
	};

	const columns: ColumnDefinition[] = [
		{
			header: 'ID',
			key: 'clientId',
			cellRenderer: (value: string) => (
				<Link to={`clients/${value}`}>
					<Typography size="sm" color={BRAND_COLORS.brand600}>
						{value}
					</Typography>
				</Link>
			)
		},
		{
			header: t('Name'),
			key: 'name',
			cellRenderer: (value: string) => (
				<Typography size="sm" color={GRAY_COLORS.gray700}>
					{value}
				</Typography>
			)
		},
		{
			header: t('Email'),
			key: 'email',
			cellRenderer: (value: string) => (
				<Typography size="sm" color="subtitle">
					{value}
				</Typography>
			)
		},
		{
			header: t('PhoneNumber'),
			key: 'size',
			cellRenderer: (value: number) => <Typography size="md">{value}</Typography>
		},
		{
			header: t('State'),
			key: 'stateOfBirth',
			cellRenderer: (value: string) => (
				<Typography size="md">{MexStates[value as keyof typeof MexStates]}</Typography>
			)
		},
		{
			header: t('Status'),
			key: 'status',
			cellRenderer: (value: ClientStatus) => {
				return <TextBadge text={ClientStatusDisplayName(value)} size="sm" />;
			}
		}
	];

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	return (
		<div className="flex flex-col py-4 gap-4">
			<div className="flex flex-col md:flex-row w-full justify-between items-center">
				<div className="inline-flex flex-row items-center gap-1">
					<div className="w-full">
						<InputField
							onChange={(e) => {
								setSearchInput(e.target.value);
							}}
							shape="pill"
							placeholder={t('Search')}
							dataTestId={'searchInput'}
							startAction={<Search size={20} />}
						/>
					</div>
				</div>
				<div className="hidden md:block mx-auto w-full md:w-auto my-4 md:my-0 md:mx-0">
					{/* <Button label={t('AskAI')} size={'auto'} onClick={() => {}} disabled /> */}
				</div>
			</div>
			<Table
				dataTestId="ClientProspectusTable"
				columns={columns}
				data={filteredClientsProspectus}
				className="custom-scrollbar"
				maxHeight={'60vh'}
			/>
		</div>
	);
};

export default ClientProspectusTable;
